/**
 * תיבת נכנס — every inbound WhatsApp reply (niv_inbox). Unread first; chips
 * for channel / identity / kind; mark read; manual identity link.
 * Error codes: CK-10502 (mark read), CK-10503 (identity), CK-10506 (thread).
 */
import { useMemo, useState } from "react";
import { WhatsappLogo, Check, ArrowCounterClockwise, UserCircle, Prohibit, CursorClick, Buildings, ChatsCircle } from "@phosphor-icons/react";
import ContentLoader from "../../../../components/shared/ContentLoader";
import LoaderLoop from "../../../../components/shared/loader/LoaderLoop";
import { useToast } from "../../../../contexts/ToastContext";
import { useGetNivInboxQuery, useMarkInboxReadMutation, useSetInboxIdentityMutation } from "../../../../store/services/nivApi";
import { IDENTITY_LABELS, INBOX_KIND_LABELS, formatWhen } from "./messagesShared";
import { INBOX_QUERY_ARGS } from "./NivMessagesTab";
import InboxThread from "./InboxThread";

/** "מטפל/ת X" for owners (the sender) and for patients (their therapist). */
const therapistLabel = (identity) => {
  if (!identity) return null;
  if (identity.kind === "owner") return identity.therapistName || identity.name || null;
  return identity.therapistName || null;
};

const FILTERS = [
  { id: "all", label: "הכל" },
  { id: "unread", label: "לא נקראו" },
  { id: "opt_out", label: "הסרות" },
  { id: "button", label: "כפתורים" },
  { id: "unknown", label: "לא מזוהים" },
];

function IdentityForm({ row, onSave, saving }) {
  const [kind, setKind] = useState(row.identity?.kind || "unknown");
  const [clinicId, setClinicId] = useState(row.identity?.clinicId || "");
  const [refId, setRefId] = useState(row.identity?.refId || "");
  const [name, setName] = useState(row.identity?.name || "");
  return (
    <div className="nmsg-identity-form">
      <select className="nmsg-select" value={kind} onChange={(e) => setKind(e.target.value)} aria-label="סוג זהות">
        {Object.entries(IDENTITY_LABELS).map(([k, l]) => (
          <option key={k} value={k}>
            {l}
          </option>
        ))}
      </select>
      <input className="nmsg-input" placeholder="שם" value={name} onChange={(e) => setName(e.target.value)} aria-label="שם" />
      <input className="nmsg-input" dir="ltr" placeholder="clinicId" value={clinicId} onChange={(e) => setClinicId(e.target.value)} aria-label="מזהה קליניקה" />
      <input className="nmsg-input" dir="ltr" placeholder="refId (patient / lead / prospect)" value={refId} onChange={(e) => setRefId(e.target.value)} aria-label="מזהה ישות" />
      <button
        type="button"
        className="niv-btn niv-btn-primary niv-btn-sm"
        disabled={saving}
        onClick={() => onSave({ kind, clinicId: clinicId.trim() || null, refId: refId.trim() || null, name: name.trim() || null })}
      >
        {saving ? <LoaderLoop size="xs" /> : <Check size={14} />} שמור שיוך
      </button>
    </div>
  );
}

export default function InboxPanel({ isAuthorized }) {
  const { showSuccess, showError } = useToast();
  const { data, isLoading, isFetching, refetch } = useGetNivInboxQuery(INBOX_QUERY_ARGS, { skip: !isAuthorized });
  const [markRead] = useMarkInboxReadMutation();
  const [setIdentity, { isLoading: savingIdentity }] = useSetInboxIdentityMutation();
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [openThread, setOpenThread] = useState(null);

  const items = useMemo(() => {
    const list = data?.items || [];
    const filtered = list.filter((r) => {
      if (filter === "unread") return !r.readAt;
      if (filter === "opt_out") return r.kind === "opt_out" || r.intent?.intent === "optout";
      if (filter === "button") return r.kind === "button" || r.kind === "button_unknown";
      if (filter === "unknown") return !r.identity || r.identity.kind === "unknown";
      return true;
    });
    return [...filtered].sort((a, b) => (a.readAt ? 1 : 0) - (b.readAt ? 1 : 0) || (b.receivedAt || "").localeCompare(a.receivedAt || ""));
  }, [data, filter]);

  const toggleRead = async (row) => {
    try {
      await markRead({ inboxId: row.id, read: !row.readAt }).unwrap();
    } catch (error) {
      console.error("[CK-10502] Inbox mark read failed:", {
        errorCode: "CK-10502",
        errorMessage: error?.message,
        inboxId: row.id,
        timestamp: new Date().toISOString(),
      });
      showError("עדכון הסטטוס נכשל");
    }
  };

  const saveIdentity = async (row, identity) => {
    try {
      await setIdentity({ inboxId: row.id, identity }).unwrap();
      setEditing(null);
      showSuccess("השיוך נשמר");
    } catch (error) {
      console.error("[CK-10503] Inbox identity save failed:", {
        errorCode: "CK-10503",
        errorMessage: error?.message,
        inboxId: row.id,
        timestamp: new Date().toISOString(),
      });
      showError(error?.message || "שמירת השיוך נכשלה");
    }
  };

  return (
    <div className="nmsg-panel">
      <div className="nmsg-toolbar">
        <div className="nmsg-filters" role="group" aria-label="סינון">
          {FILTERS.map((f) => (
            <button key={f.id} type="button" className={`nmsg-filter ${filter === f.id ? "active" : ""} ${f.id === "opt_out" ? "tone-bad" : ""}`} onClick={() => setFilter(f.id)}>
              {f.label}
              {f.id === "unread" && data?.unreadCount > 0 && <span className="nmsg-count">{data.unreadCount}</span>}
            </button>
          ))}
        </div>
        <button type="button" className="niv-btn niv-btn-secondary niv-btn-sm" onClick={() => refetch()} disabled={isFetching}>
          <ArrowCounterClockwise size={14} className={isFetching ? "spinning" : ""} /> רענון
        </button>
      </div>

      {isLoading ? (
        <ContentLoader size="sm" compact message="טוען…" />
      ) : items.length === 0 ? (
        <div className="nmsg-empty">
          <WhatsappLogo size={36} weight="light" />
          <p>אין הודעות נכנסות{filter !== "all" ? " בסינון הזה" : ""}.</p>
        </div>
      ) : (
        <div className="nmsg-rows">
          {items.map((row) => {
            const unread = !row.readAt;
            const isOptOut = row.kind === "opt_out" || row.intent?.intent === "optout";
            const tone = isOptOut ? "failed" : row.kind === "button" ? "sms" : unread ? "whatsapp" : "muted";
            return (
              <div key={row.id} className={`nmsg-row nmsg-row-${tone} ${unread ? "is-unread" : ""}`}>
                <span className={`nmsg-dot nmsg-dot-${tone}`} aria-hidden="true" />
                <div className="nmsg-row-main">
                  <div className="nmsg-row-top">
                    <strong className="nmsg-row-name">{row.contactName || row.identity?.name || row.from}</strong>
                    <span className={`nmsg-chip nmsg-chip-identity-${row.identity?.kind || "unknown"}`}>
                      <UserCircle size={11} weight="fill" /> {IDENTITY_LABELS[row.identity?.kind] || IDENTITY_LABELS.unknown}
                      {row.identity?.matches > 1 ? ` (${row.identity.matches})` : ""}
                    </span>
                    {isOptOut ? (
                      <span className="nmsg-chip nmsg-chip-bad">
                        <Prohibit size={11} weight="fill" /> הסרה
                      </span>
                    ) : (
                      <span className="nmsg-chip nmsg-chip-muted">
                        {row.kind === "button" && <CursorClick size={11} weight="fill" />} {INBOX_KIND_LABELS[row.kind] || row.kind}
                        {row.intent ? ` · ${row.intent.intent}` : ""}
                      </span>
                    )}
                    <span className="nmsg-row-time">{formatWhen(row.receivedAt)}</span>
                  </div>
                  <p className="nmsg-row-preview" dir="auto">
                    {row.preview || "—"}
                  </p>
                  <div className="nmsg-row-meta">
                    <span dir="ltr">{row.from}</span>
                    {row.identity?.clinicId && (
                      <span title={row.identity.clinicId}>
                        <Buildings size={11} weight="fill" /> {row.identity.clinicName || `קליניקה ${row.identity.clinicId}`}
                      </span>
                    )}
                    {therapistLabel(row.identity) && (
                      <span title={row.identity.therapistId || (row.identity.kind === "owner" ? row.identity.refId : undefined)}>
                        <UserCircle size={11} weight="fill" /> {therapistLabel(row.identity)}
                      </span>
                    )}
                    {row.identity?.kind === "patient" && (row.identity.refName || row.identity.name) && (
                      <span title={row.identity.refId}>מטופל/ת: {row.identity.refName || row.identity.name}</span>
                    )}
                    {row.identity?.refId && row.identity.kind !== "patient" && row.identity.kind !== "owner" && (
                      <span dir="ltr" title="מזהה רשומה">
                        {row.identity.refId}
                      </span>
                    )}
                    {row.optOutApplied?.length > 0 && <span>הוסר מ-{row.optOutApplied.length} רשומות</span>}
                  </div>
                  {editing === row.id && <IdentityForm row={row} saving={savingIdentity} onSave={(identity) => saveIdentity(row, identity)} />}
                  {openThread === row.id && (
                    <div className="nmsg-row-detail">
                      <InboxThread waId={row.waId} highlightAt={row.receivedAt} />
                    </div>
                  )}
                </div>
                <div className="nmsg-row-actions">
                  <button type="button" className="niv-btn niv-btn-secondary niv-btn-sm" onClick={() => toggleRead(row)}>
                    {unread ? <Check size={14} /> : <ArrowCounterClockwise size={14} />} {unread ? "סמן כנטופל" : "החזר ללא נקרא"}
                  </button>
                  <button
                    type="button"
                    className={`niv-btn niv-btn-secondary niv-btn-sm${openThread === row.id ? " is-active" : ""}`}
                    onClick={() => setOpenThread(openThread === row.id ? null : row.id)}
                    aria-expanded={openThread === row.id}
                  >
                    <ChatsCircle size={14} /> {openThread === row.id ? "סגור שיחה" : "כל השיחה"}
                  </button>
                  <button type="button" className="niv-btn niv-btn-secondary niv-btn-sm" onClick={() => setEditing(editing === row.id ? null : row.id)}>
                    <UserCircle size={14} /> שייך ל…
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
